import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useGame } from "../context/GameContext";
import PageLayout from "../components/PageLayout";
import MdGlowButton from "../components/MdGlowButton";
import { Button } from "../components/ui/button";
import { playGameSample, unlockAudioContext, playAudioArrayBuffer } from "../lib/playAudio";
import { PlayCircle, Volume2, Mic, Square } from "lucide-react";

const SOUND_TEST_SRC = `${process.env.PUBLIC_URL || ""}/audio/alarme.mp3`;
const MIC_MAX_RECORD_MS = 8000;

function isLikelyIOS() {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1)
  );
}

function getAudioRecorderMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  // Android Chrome → webm ; iOS → mp4
  const types = isLikelyIOS()
    ? ["audio/mp4", "audio/aac", "audio/wav", "audio/webm"]
    : ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
  return types.find((type) => MediaRecorder.isTypeSupported(type)) || "";
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const Welcome = () => {
  const navigate = useNavigate();
  const { token } = useGame();
  const [soundTesting, setSoundTesting] = useState(false);
  const [micRecording, setMicRecording] = useState(false);
  const [micTake, setMicTake] = useState(0);
  const [hasMicTake, setHasMicTake] = useState(false);
  const [micPlaying, setMicPlaying] = useState(false);
  const [micError, setMicError] = useState("");

  const micRecorderRef = useRef(null);
  const micChunksRef = useRef([]);
  const micStreamRef = useRef(null);
  const micStopTimerRef = useRef(null);
  const micBlobRef = useRef(null); // source de vérité (pas d’URL en cache)
  const micMimeRef = useRef("audio/webm");
  const micGenRef = useRef(0);
  const micStartingRef = useRef(false);
  const micStopWaitRef = useRef(null);

  useEffect(() => {
    if (!token) navigate("/");
  }, [token, navigate]);

  const clearMicPlayback = useCallback(() => {
    micBlobRef.current = null;
    setHasMicTake(false);
    setMicPlaying(false);
  }, []);

  const stopTracks = useCallback(() => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          /* ignore */
        }
      });
      micStreamRef.current = null;
    }
  }, []);

  /** Stoppe l'enregistreur et attend onstop (évite de garder l'ancien take). */
  const stopRecorderAndWait = useCallback(async () => {
    if (micStopTimerRef.current) {
      clearTimeout(micStopTimerRef.current);
      micStopTimerRef.current = null;
    }
    const recorder = micRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      micRecorderRef.current = null;
      stopTracks();
      setMicRecording(false);
      return;
    }
    await new Promise((resolve) => {
      const done = () => {
        resolve();
      };
      micStopWaitRef.current = done;
      try {
        recorder.requestData?.();
      } catch {
        /* ignore */
      }
      try {
        recorder.stop();
      } catch {
        done();
      }
      // Sécurité si onstop ne fire pas
      setTimeout(done, 1500);
    });
    micStopWaitRef.current = null;
    micRecorderRef.current = null;
    stopTracks();
    setMicRecording(false);
  }, [stopTracks]);

  useEffect(
    () => () => {
      micGenRef.current += 1;
      if (micStopTimerRef.current) clearTimeout(micStopTimerRef.current);
      try {
        micRecorderRef.current?.stop();
      } catch {
        /* ignore */
      }
      stopTracks();
      clearMicPlayback();
    },
    [stopTracks, clearMicPlayback]
  );

  const testSound = async () => {
    setSoundTesting(true);
    try {
      await unlockAudioContext();
      await playGameSample(SOUND_TEST_SRC, 1);
    } finally {
      setSoundTesting(false);
    }
  };

  const stopMicRecording = useCallback(() => {
    void stopRecorderAndWait();
  }, [stopRecorderAndWait]);

  const startMicRecording = async () => {
    if (micStartingRef.current || micRecording) return;
    micStartingRef.current = true;
    setMicError("");
    setMicPlaying(false);

    // Invalide l'ancien take AVANT tout
    micGenRef.current += 1;
    const gen = micGenRef.current;
    clearMicPlayback();
    await stopRecorderAndWait();
    await wait(200);

    try {
      await unlockAudioContext();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      if (gen !== micGenRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      micStreamRef.current = stream;
      micChunksRef.current = [];
      const mimeType = getAudioRecorderMimeType();
      micMimeRef.current = mimeType || (isLikelyIOS() ? "audio/mp4" : "audio/webm");
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      micRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (gen !== micGenRef.current) return;
        if (event.data?.size > 0) micChunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        const notifyWait = micStopWaitRef.current;
        if (gen !== micGenRef.current) {
          notifyWait?.();
          return;
        }
        const chunks = micChunksRef.current.slice();
        micChunksRef.current = [];
        const type = recorder.mimeType || micMimeRef.current;
        const blob = new Blob(chunks, { type });
        if (blob.size > 0) {
          micBlobRef.current = blob;
          micMimeRef.current = type;
          setMicTake((n) => n + 1);
          setHasMicTake(true);
          setMicError("");
        } else {
          micBlobRef.current = null;
          setHasMicTake(false);
          setMicError("Aucun son enregistré. Réessayez en parlant plus fort.");
        }
        stopTracks();
        if (micRecorderRef.current === recorder) micRecorderRef.current = null;
        setMicRecording(false);
        notifyWait?.();
      };

      try {
        recorder.start(200);
      } catch {
        recorder.start();
      }
      setMicRecording(true);
      micStopTimerRef.current = setTimeout(() => {
        if (gen === micGenRef.current) void stopRecorderAndWait();
      }, MIC_MAX_RECORD_MS);
    } catch {
      setMicError("Micro inaccessible. Autorisez le micro dans votre navigateur.");
      stopTracks();
      setMicRecording(false);
    } finally {
      micStartingRef.current = false;
    }
  };

  const playMicPlayback = async () => {
    const blob = micBlobRef.current;
    if (!blob) {
      setMicError("Aucun enregistrement. Appuyez sur Enregistrer.");
      return;
    }
    setMicError("");
    setMicPlaying(true);
    try {
      await unlockAudioContext();
      const buffer = await blob.arrayBuffer();
      // Copie pour éviter ArrayBuffer détaché si on réécoute
      const copy = buffer.slice(0);
      await playAudioArrayBuffer(copy, 1, { mimeType: micMimeRef.current || blob.type || "audio/webm" });
    } catch {
      setMicError("Impossible de lire l'enregistrement.");
    } finally {
      setMicPlaying(false);
    }
  };

  return (
    <PageLayout wide>
      <div className="text-center fade-up max-w-2xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight mb-3">
          Vérifiez votre <span className="text-md-cyan">matériel</span>
        </h2>
        <p className="text-white/55 text-sm sm:text-base leading-relaxed">
          Avant de commencer, testez le son de votre appareil et votre microphone.
        </p>
      </div>

      <section className="mt-8 max-w-4xl mx-auto fade-up" data-testid="welcome-equipment">
        <div className="md-card overflow-hidden">
          <div className="grid sm:grid-cols-2 sm:divide-x sm:divide-white/10">
            <div className="p-5 border-b sm:border-b-0 border-white/10 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Volume2 className="text-md-cyan shrink-0" size={18} />
                <h4 className="font-semibold text-white text-sm">Son de l&apos;appareil</h4>
              </div>
              <p className="text-sm text-white/55 leading-snug mb-4">
                Mettez le volume à environ <strong className="text-white/80">50 %</strong>, puis
                testez les effets sonores.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void testSound()}
                disabled={soundTesting}
                className="shrink-0 border-md-cyan/30 bg-md-cyan/10 text-md-cyan hover:bg-md-cyan/20 hover:text-white"
                data-testid="welcome-sound-test"
              >
                <Volume2 size={14} className="mr-1.5" />
                {soundTesting ? "Lecture…" : "Tester le son"}
              </Button>
              <p className="text-sm text-white/55 leading-snug mt-3">
                Si vous entendez l&apos;alarme de départ du chronomètre, le son fonctionne correctement sur votre
                appareil.
              </p>
            </div>

            <div className="p-5 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Mic className="text-md-cyan shrink-0" size={18} />
                <h4 className="font-semibold text-white text-sm">Microphone</h4>
              </div>
              <p className="text-sm text-white/55 leading-snug mb-4">
                Enregistrez quelques secondes de votre voix, puis écoutez l&apos;enregistrement pour vérifier que votre
                micro fonctionne.
              </p>
              <div className="flex flex-col items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => (micRecording ? stopMicRecording() : void startMicRecording())}
                  className="shrink-0 border-md-cyan/30 bg-md-cyan/10 text-md-cyan hover:bg-md-cyan/20 hover:text-white"
                  data-testid="welcome-mic-test"
                >
                  {micRecording ? (
                    <>
                      <Square size={14} className="mr-1.5 fill-current" />
                      Arrêter l&apos;enregistrement
                    </>
                  ) : (
                    <>
                      <Mic size={14} className="mr-1.5" />
                      {hasMicTake ? "Réenregistrer" : "Enregistrer"}
                    </>
                  )}
                </Button>
                {hasMicTake && !micRecording ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void playMicPlayback()}
                    disabled={micPlaying}
                    className="shrink-0 border-md-cyan/30 bg-md-cyan/10 text-md-cyan hover:bg-md-cyan/20 hover:text-white"
                    data-testid="welcome-mic-playback"
                  >
                    <Volume2 size={14} className="mr-1.5" />
                    {micPlaying
                      ? "Lecture…"
                      : `Écouter l'enregistrement${micTake > 0 ? ` (#${micTake})` : ""}`}
                  </Button>
                ) : null}
              </div>
              {hasMicTake && !micRecording ? (
                <p className="text-xs text-white/45 mt-2">Prise n°{micTake} prête — réenregistrer remplace l&apos;ancienne.</p>
              ) : null}
              <p className="text-sm text-white/55 leading-snug mt-3">
                Si vous entendez vos voix, le micro fonctionne correctement.
              </p>
              <div
                className="md-card mt-4 p-4 text-center border border-amber-400/25 bg-amber-400/5"
                data-testid="welcome-mic-fan-tip"
              >
                <div className="text-sm leading-relaxed">
                  <p className="font-semibold text-amber-200 mb-1">Attention au micro</p>
                  <p className="text-white/80">
                    <strong className="text-white font-bold">
                      Si vous avez un ventilateur ou une climatisation, ne l&apos;orientez pas vers
                      le micro / l&apos;appareil
                    </strong>
                    . Le souffle peut saturer le micro et couvrir tous les autres sons.
                  </p>
                </div>
              </div>
              {micRecording ? (
                <p className="text-xs text-md-cyan mt-3">Enregistrement en cours… Parlez maintenant.</p>
              ) : null}
              {micError ? <p className="text-xs text-amber-300 mt-3">{micError}</p> : null}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-md mx-auto">
        <MdGlowButton
          onClick={() => navigate("/comment-jouer")}
          wrapClassName="mt-8"
          data-testid="welcome-continue-button"
        >
          <PlayCircle className="w-5 h-5 shrink-0" />
          Continuer vers le tutoriel
        </MdGlowButton>
      </div>
    </PageLayout>
  );
};

export default Welcome;
